/**
 * 連続スクロールカレンダービュー
 *
 * 日付で区切らず、ひとつながりの時間軸として過去へさかのぼれるカレンダー。
 * 上へスクロールするとさらに過去の日を読み込む。
 */

import { useMemo } from 'react'

import { Skeleton } from '@/components/ui/skeleton'
import { useGarminStore } from '@/store/useGarminStore'

import { useCalendarExtras } from '../use-calendar-extras'
import { useCalendarFilterStore } from '../use-calendar-filter'
import { useActivityDayPaging } from '../use-day-paging'
import { buildEventsFromDays, getDayBounds } from './calendar-event-builder'
import { CalendarFilterBar } from './calendar-filter-bar'
import { ContinuousCalendarGrid } from './continuous-calendar-grid'
import { useCalendarSheets } from './use-calendar-sheets'

type Props = {
  /** ヘッダで選んだ日付(YYYY-MM-DD)。変わるとその日までスクロールする */
  targetDate?: string
}

export const InfiniteCalendarView = ({ targetDate }: Props) => {
  const activities = useGarminStore((state) => state.activities)
  const { handleEventSelect, sheets } = useCalendarSheets()
  const hiddenTypes = useCalendarFilterStore((state) => state.hiddenTypes)
  const { dayDataMap, loadedDates, isLoadingMore, loadMorePast, pendingJumpDate, clearPendingJump } =
    useActivityDayPaging(targetDate)

  // 読み込み済みの全日分をひとつの期間としてまとめる
  const { rangeStart, rangeEnd, dayCount } = useMemo(() => {
    const oldestDate = loadedDates.at(-1)
    const newestDate = loadedDates.at(0)
    if (!oldestDate || !newestDate) {
      const now = new Date()
      return { rangeStart: now, rangeEnd: now, dayCount: 0 }
    }

    const { dayStart } = getDayBounds(new Date(`${oldestDate}T00:00:00`))
    const { dayEnd } = getDayBounds(new Date(`${newestDate}T00:00:00`))
    return { rangeStart: dayStart, rangeEnd: dayEnd, dayCount: loadedDates.length }
  }, [loadedDates])

  const { workouts, todos } = useCalendarExtras({ rangeStart, rangeEnd })

  const events = useMemo(() => {
    if (dayCount === 0) return []
    const days = loadedDates.flatMap((dateStr) => {
      const dayData = dayDataMap.get(dateStr)
      return dayData ? [dayData] : []
    })
    const all = buildEventsFromDays(days, { activities, workouts, todos }, rangeStart, rangeEnd)
    return all.filter((event) => !hiddenTypes.includes(event.props.type))
  }, [dayCount, loadedDates, dayDataMap, activities, workouts, todos, rangeStart, rangeEnd, hiddenTypes])

  if (dayCount === 0) {
    return (
      <div className="space-y-4 p-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    )
  }

  return (
    <div className="activity-timeline flex h-full flex-col">
      <div className="border-b">
        <CalendarFilterBar />
      </div>

      <ContinuousCalendarGrid
        rangeStart={rangeStart}
        dayCount={dayCount}
        events={events}
        onEventSelect={handleEventSelect}
        onLoadMorePast={loadMorePast}
        isLoadingMore={isLoadingMore}
        scrollToDate={pendingJumpDate}
        onScrolledToDate={clearPendingJump}
      />

      {sheets}
    </div>
  )
}
